import api from "../api/api";
import ITenantInMarket, { ITenantInMarketRequestModel, ITenantInMarketResponseModel, ITenantInMarketState } from "../interfaces/ITenantInMarket";
import Shape from "../interfaces/Shape";
import { parseDateTime } from "../utils/utils";

export default class TenantsInMarketApiService {
    public async getTenantsInMarket(): Promise<ITenantInMarket[]> {
        const response = await api.get<ITenantInMarketResponseModel[]>("/tenantinmarket/get");
        return this.mapRequestModelsToTenantsInMarket(response.data);
    }

    public async createTenantInMarket(tenantInMarketState: ITenantInMarketState, pipedriveCompanyId: number | null, pipedriveBrokerageId: number | null, shape: Shape, geography: string): Promise<{ id: number, modifiedDate: Date, modifiedByUser: string }> {
        const response = await api.post("/tenantinmarket/create", this.mapTenantInMarketStateToRequestModel(tenantInMarketState, pipedriveCompanyId, pipedriveBrokerageId, shape, geography));

        return {
            id: response.data.id,
            modifiedDate: parseDateTime(response.data.modifiedDate)!,
            modifiedByUser: response.data.modifiedByUser,
        };
    }

    public async updateTenantInMarket(tenantInMarket: ITenantInMarket): Promise<{ modifiedDate: Date, modifiedByUser: string }> {
        const response = await api.post("/tenantinmarket/update", this.mapTenantInMarketToRequestModel(tenantInMarket));

        return {
            modifiedDate: parseDateTime(response.data.modifiedDate)!,
            modifiedByUser: response.data.modifiedByUser,
        };
    }

    public async deleteTenantInMarket(id: number): Promise<void> {
        await api.delete(`/tenantinmarket/delete?id=${id}`);
    }

    private mapRequestModelsToTenantsInMarket(tenantsInMarket: ITenantInMarketResponseModel[]): ITenantInMarket[] {
        return tenantsInMarket.map(tim => ({
            id: tim.id,
            shape: new Shape(tim.shape),
            description: tim.description,
            pipedriveCompanyId: tim.pipedriveCompanyId,
            pipedriveBrokerageId: tim.pipedriveBrokerageId,
            landAreaMin: tim.landAreaMin,
            landAreaMax: tim.landAreaMax,
            buildingAreaMin: tim.buildingAreaMin,
            buildingAreaMax: tim.buildingAreaMax,
            notes: tim.notes,
            useType: tim.useType,
            contact: tim.contact,
            targetAnnualRent: tim.targetAnnualRent,
            zoningRequired: tim.zoningRequired,
            siteConditions: tim.siteConditions,
            timing: tim.timing,
            companyWebsite: tim.companyWebsite,
            corpOwnership: tim.corpOwnership,
            revenue: tim.revenue,
            numberOfEmployees: tim.numberOfEmployees,
            numberOfLocations: tim.numberOfLocations,
            inactiveReason: tim.inactiveReason,
            assignedToPipedriveUserId: tim.assignedToPipedriveUserId,
            modifiedByUser: tim.modifiedByUser,
            modifiedDate: parseDateTime(tim.modifiedDate),
            createdAt: parseDateTime(tim.createdAt),
        }));
    }

    private mapTenantInMarketStateToRequestModel(tenantInMarketState: ITenantInMarketState, pipedriveCompanyId: number | null, pipedriveBrokerageId: number | null, shape: Shape, geography: string): ITenantInMarketRequestModel {
        return {
            description: tenantInMarketState.description,
            pipedriveCompanyId: pipedriveCompanyId,
            pipedriveBrokerageId: pipedriveBrokerageId,
            shape: shape.points,
            landAreaMin: tenantInMarketState.landAreaMin!,
            landAreaMax: tenantInMarketState.landAreaMax!,
            buildingAreaMin: tenantInMarketState.buildingAreaMin!,
            buildingAreaMax: tenantInMarketState.buildingAreaMax!,
            notes: tenantInMarketState.notes,
            useType: tenantInMarketState.useType,
            contact: tenantInMarketState.contact,
            targetAnnualRent: tenantInMarketState.targetAnnualRent,
            zoningRequired: tenantInMarketState.zoningRequired,
            siteConditions: tenantInMarketState.siteConditions,
            timing: tenantInMarketState.timing,
            companyWebsite: tenantInMarketState.companyWebsite,
            corpOwnership: tenantInMarketState.corpOwnership,
            revenue: tenantInMarketState.revenue,
            numberOfEmployees: tenantInMarketState.numberOfEmployees,
            numberOfLocations: tenantInMarketState.numberOfLocations,
            assignedToPipedriveUserId: tenantInMarketState.assignedToPipedriveUserId!,
            inactiveReason: tenantInMarketState.inactiveReason,
            geography: geography,
        };
    }

    private mapTenantInMarketToRequestModel(tenantInMarket: ITenantInMarket): ITenantInMarketRequestModel {
        return {
            id: tenantInMarket.id,
            description: tenantInMarket.description,
            pipedriveCompanyId: tenantInMarket.pipedriveCompanyId,
            pipedriveBrokerageId: tenantInMarket.pipedriveBrokerageId,
            shape: tenantInMarket.shape.points,
            landAreaMin: tenantInMarket.landAreaMin,
            landAreaMax: tenantInMarket.landAreaMax,
            buildingAreaMin: tenantInMarket.buildingAreaMin,
            buildingAreaMax: tenantInMarket.buildingAreaMax,
            notes: tenantInMarket.notes,
            useType: tenantInMarket.useType,
            contact: tenantInMarket.contact,
            targetAnnualRent: tenantInMarket.targetAnnualRent,
            zoningRequired: tenantInMarket.zoningRequired,
            siteConditions: tenantInMarket.siteConditions,
            timing: tenantInMarket.timing,
            companyWebsite: tenantInMarket.companyWebsite,
            corpOwnership: tenantInMarket.corpOwnership,
            revenue: tenantInMarket.revenue,
            numberOfEmployees: tenantInMarket.numberOfEmployees,
            numberOfLocations: tenantInMarket.numberOfLocations,
            assignedToPipedriveUserId: tenantInMarket.assignedToPipedriveUserId,
            inactiveReason: tenantInMarket.inactiveReason,
        };
    }
}
